import autoBind from "auto-bind";
import { kebabCase } from "lodash";
import { Author } from "../../manifest/models/author.models";
import {
  FollowupAction,
  PostComment,
  PostData,
  ReplyFollowup,
} from "../../posts/post.models";
import { CyclingBank } from "../../utils/collections/cycling_bank";
import { ShuffledCyclingBank } from "../../utils/collections/shuffled_cycling_bank";
import { DateFormatter } from "../../utils/date_formatter";
import { CommentDateRandomizer } from "../../utils/date_randomization";
import { randomNumber } from "../../utils/randomization";
import { getUsernameFor } from "../../utils/username";
import { CommentGenerationStrategy } from "../generation.models";
import { AuthorGenerator } from "./author.generator";

export class CommentGenerator {
  readonly bank: { [tag: string]: CyclingBank<string> };
  readonly tagBank: CyclingBank<string>;

  private strategy: CommentGenerationStrategy;
  private authorGenerator?: AuthorGenerator;
  private formatter: DateFormatter;
  readonly condition: string;

  constructor(
    bank: { [tag: string]: string[] },
    strategy: CommentGenerationStrategy,
    condition: string,
    authorGenerator?: AuthorGenerator,
  ) {
    this.bank = {};
    this.condition = condition;
    const tags = Object.keys(bank);
    this.tagBank = new CyclingBank(tags);
    console.debug("Generating comments using these tags", tags);

    for (const key of tags) {
      const comments = bank[key];
      this.bank[key] = new CyclingBank(comments);
    }

    this.strategy = strategy;
    this.authorGenerator = authorGenerator;

    this.formatter = new DateFormatter();

    if (this.strategy.date_generation == null) {
      throw new Error(
        "FATAL: Date Generation Strategy not found for comments!",
      );
    }

    autoBind(this);
  }

  _addRandomNumComments(data: PostData[]): PostData[] {
    return data.map((post, index) => {
      const addComments = post.includesComments ?? true;
      if (!addComments) return { ...post, comments: [] };

      const min = this.strategy.min_comments;
      let max = this.strategy.max_comments;

      if (this.strategy.increase_per_post != null) {
        max += this.strategy.increase_per_post * index;
      }

      if (this.strategy.max_cap != null) {
        max = Math.min(this.strategy.max_cap, max);
      }

      let comments: PostComment[] = [];
      let numComments = randomNumber(min, max);
      const randomizer = new CommentDateRandomizer(post, numComments);
      let tags = post.tags ?? ["default"];
      if (tags.length == 0) tags.push("default");

      let tagBank = new ShuffledCyclingBank(tags);

      for (let i = 0; i < numComments; i++) {
        let comment;

        const tag = tagBank.getNextObject();
        const bank = this.bank[tag];

        if (bank == null) {
          console.debug("post that caused error", post);
          console.debug("available banks when error occurred", this.bank);
          throw new Error(
            `FATAL: Cannot generate comment from tag ${tag} as no bank exists for that tag!`,
          );
        }

        comment = bank.getNextObject();
        const author = this._grabAuthorFor(post);
        comments.push({
          id: commentToId(comment),
          body: comment,
          date: this.formatter.formatOffsetAsDate(randomizer.next()),
          avatar: author!.avatar!.filename,
          author: author != null ? getUsernameFor(author) : undefined,
        });
      }

      return {
        ...post,
        comments: comments,
        userFollowup: this._getReplyFollowup(post),
      };
    });
  }

  _addAllAvailableComments(data: PostData[]): PostData[] {
    return data.map((post) => {
      let tags = post.tags ?? ["default"];
      if (tags.length == 0) tags.push("default");
      let tagBank = new ShuffledCyclingBank(tags);
      const comments: PostComment[] = [];
      const allComments = tags
        .map((tag) => this.bank[tag])
        .filter((value) => {
          return value != null;
        })
        .map((bank) => bank.bank);

      if (allComments.length == 0) {
        return {
          ...post,
        };
      }

      const numComments = allComments
        .map((x) => x.length)
        .reduce((a, b) => a + b);
      const randomizer = new CommentDateRandomizer(post, numComments);

      for (let i = 0; i < tags.length; i++) {
        const tag = tagBank.getNextObject();
        const initial = this.bank[tag];
        if (!initial) continue;

        const bank = new ShuffledCyclingBank(this.bank[tag].bank);

        for (const comment of bank.bank) {
          const author = this._grabAuthorFor(post);

          comments.push({
            id: commentToId(comment),
            body: comment,
            date: this.formatter.formatOffsetAsDate(randomizer.next()),
            avatar: author!.avatar!.filename,
            author: author != null ? getUsernameFor(author) : undefined,
          });
        }
      }

      return {
        ...post,
        comments: comments,
        userFollowup: this._getReplyFollowup(post),
      };
    });
  }

  _grabAuthorFor(post: PostData): Author {
    if (this.authorGenerator == null)
      throw new Error(
        `No author generator available to grab comment author for post with id ${post.id}`,
      );
    let author = this.authorGenerator!.getNextObject();

    while (getUsernameFor(author) === post.author) {
      author = this.authorGenerator!.getNextObject();
      console.debug(
        `choosing new comment author for post with author ${post.author} to prevent duplicate`,
      );
    }

    return author;
  }

  _getReplyFollowup(post: PostData): ReplyFollowup | undefined {
    if (!this.strategy.comment_followup?.enabled) return undefined;
    const strategy = this.strategy.comment_followup!;

    if (strategy.action == "like") {
      var user: Author = this._grabAuthorFor(post);

      if (strategy.usernameConditionOverrides != null) {
        const override = strategy.usernameConditionOverrides[this.condition];

        if (override != null) {
          user = {
            ...user,
            first: override,
            last: "",
          };
        }
      }

      return {
        action: FollowupAction.like,
        delay: randomNumber(strategy.delayMin, strategy.delayMax),
        user: user,

        metadata: {},
      };
    } else {
      return undefined;
    }
  }

  addPostComments(data: PostData[]): PostData[] {
    if (this.strategy.use_all_matching)
      return this._addAllAvailableComments(data);
    return this._addRandomNumComments(data);
  }
}

export function commentToId(comment: string): string {
  return kebabCase(comment.substring(0, 10));
}
